import { useCallback, useEffect, useRef, useState } from "react"
import { useLocation } from "react-router-dom"
import { Button } from "antd"

/**
 * Detects a new deploy and gets users onto it without a manual Ctrl+F5.
 *
 * The production build references its bundle as /static/js/main.<hash>.js. We
 * compare the bundle this tab loaded with the one in a fresh /index.html:
 *  - first check right after load (stale cached index.html): reload at once;
 *  - tab open during a deploy: a small “New version” bar, and an automatic
 *    reload on the next navigation (nothing unsaved is lost by leaving a page);
 *  - re-checked every 5 minutes and when the tab regains focus.
 * Does nothing in development (no hashed main bundle).
 */
const MAIN_BUNDLE = /\/static\/js\/main\.[A-Za-z0-9]+\.js/
const CHECK_EVERY_MS = 5 * 60 * 1000
const RELOADED_FOR_KEY = "viridian-reloaded-for-bundle"

const loadedBundle = (): string | null => {
  for (const s of Array.from(document.scripts)) {
    const m = MAIN_BUNDLE.exec(s.src)
    if (m) return m[0]
  }
  return null
}

const latestBundle = async (): Promise<string | null> => {
  try {
    const res = await fetch(`/index.html?_=${Date.now()}`, { cache: "no-store" })
    if (!res.ok) return null
    return MAIN_BUNDLE.exec(await res.text())?.[0] ?? null
  } catch {
    return null
  }
}

const readGuard = (): string | null => {
  try { return sessionStorage.getItem(RELOADED_FOR_KEY) } catch { return null }
}

export const AppUpdateWatcher = () => {
  const location = useLocation()
  const [ latest, setLatest ] = useState<string | null>(null)
  const firstCheckRef = useRef(true)
  const current = useRef(loadedBundle()).current

  const reload = useCallback((to?: string) => {
    if (latest) {
      try { sessionStorage.setItem(RELOADED_FOR_KEY, latest) } catch { /* ignore */ }
    }
    if (to) window.location.assign(to)
    else window.location.reload()
  }, [ latest ])

  const check = useCallback(async () => {
    if (!current) return
    const found = await latestBundle()
    if (!found || found === current) return
    setLatest(found)
    // A previous reload for this very bundle didn't help (cached index.html): don't loop.
    if (firstCheckRef.current && readGuard() !== found) {
      try { sessionStorage.setItem(RELOADED_FOR_KEY, found) } catch { /* ignore */ }
      window.location.reload()
    }
  }, [ current ])

  useEffect(() => {
    if (!current) return
    const first = window.setTimeout(() => {
      void check().finally(() => { firstCheckRef.current = false })
    }, 2000)
    const interval = window.setInterval(() => { void check() }, CHECK_EVERY_MS)
    const onVisible = () => { if (document.visibilityState === "visible") void check() }
    document.addEventListener("visibilitychange", onVisible)
    window.addEventListener("focus", onVisible)
    return () => {
      window.clearTimeout(first)
      window.clearInterval(interval)
      document.removeEventListener("visibilitychange", onVisible)
      window.removeEventListener("focus", onVisible)
    }
  }, [ current, check ])

  // Navigating to another page is a safe moment to switch versions.
  const lastPathRef = useRef(location.pathname + location.search)
  useEffect(() => {
    const path = location.pathname + location.search
    if (path === lastPathRef.current) return
    lastPathRef.current = path
    if (latest && readGuard() !== latest) reload(window.location.href)
  }, [ location, latest, reload ])

  if (!latest) return null
  return (
    <div className="app-update-bar" role="status">
      <span>A new version of Viridian is available.</span>
      <Button size="small" type="primary" onClick={() => reload()}>Reload</Button>
    </div>
  )
}
